// Level one of the two-level control on /platform/ — the seven-tab strip that picks which
// capability is showing. Level two, the carousel inside six of those seven sections, is
// modules/feature-carousel.js, and the two modules never speak to each other. They do not
// need to: hiding a panel here with `display: none` zeroes its intersection rectangle, so
// the carousel inside it learns it has gone off-duty from its own IntersectionObserver.
// That is the whole reason the panels hide with `display` while the slides one level down
// hide with `visibility` — see the note in _platform.scss before changing either.
//
// Enhancement, not construction. The markup ships every panel visible and stacked with
// the tab strip `hidden`; this module reveals the strip and only then starts hiding
// panels. So a bundle that never loads leaves a long page rather than a broken one, and
// nothing on the page is ever a control that does nothing. Undoing that ordering is the
// one change here that turns a JS failure into an unusable page.
//
// Deliberately not built on modules/use-cases.js, which is the same pattern one level up
// and worth reading first. That class couples its tab strip to a single arrow pair and a
// single progress rail, because /developer-program/ has exactly one of each; this page
// has seven sections with six independent rails beneath one strip. What is taken from it
// is the discipline — reveal before hide, `is-enhanced` as the CSS switch — not the code.

import { prefersReducedMotion } from "../helpers/reducedMotion.js";

// Has to clear the .pf-panel opacity transition in _platform.scss (260ms) rather than
// approximate it: swapping early cuts the outgoing panel off mid-fade. The 20ms margin is
// the same one use-cases.js and word-cycle.js use.
const FADE_MS = 280;

class FeatureTabs {
    constructor(elem) {
        this.elem = elem;
        this.strip = elem.querySelector("[data-feature-tabs-strip]");
        // The phone control (#226). Optional, so the strip alone still works; CSS decides
        // which of the two is on screen, and show() keeps both pointing at one section.
        this.picker = elem.querySelector("[data-feature-tabs-picker]");
        this.select = this.picker?.querySelector("select") ?? null;
        this.tabs = [...elem.querySelectorAll('[role="tab"]')];
        this.panels = [...elem.querySelectorAll("[data-feature-panel]")];
        this.index = 0;
        // What has been asked for, as opposed to what is on screen. null rather than 0 so
        // the opening show(0) is not mistaken for a repeat of the panel already showing.
        this.target = null;
        this.fadeTimer = null;

        // Every tab needs its panel and vice versa. A mismatch means the template moved
        // under the module, and switching would then hide a panel it cannot bring back —
        // leaving the stacked state alone is the safe failure.
        if (!this.strip || !this.tabs.length || this.tabs.length !== this.panels.length) return;

        this.reveal();
        this.bind();
        this.show(0, { animate: false });
    }

    // Order matters: the strip becomes visible before any panel is hidden, so there is no
    // frame in which the page shows one section and offers no way to reach the other six.
    reveal() {
        this.strip.hidden = false;
        if (this.picker) this.picker.hidden = false;

        // Last, and only once the strip exists. This is the flag _platform.scss reads to
        // switch from the plain stack to one-section-at-a-time, and to drop the rules
        // between sections that only make sense while all seven are on the page.
        this.elem.classList.add("is-enhanced");
    }

    bind() {
        this.tabs.forEach((tab, i) => {
            tab.addEventListener("click", () => {
                this.show(i);
                // Click already moves focus here; this keeps keyboard activation
                // (Enter/Space, which also arrive as click) landing in the same place.
                tab.focus();
            });

            // Roving tabindex: one tab in the page tab order at a time, arrows move
            // between them. Home/End included — with seven tabs, reaching either end
            // otherwise takes six presses.
            tab.addEventListener("keydown", this.handleKeydown);
        });

        // `change`, not `input`: on iOS the wheel fires input on every scroll step, and
        // swapping the section under the picker while it is still open is disorienting.
        this.select?.addEventListener("change", () => this.show(Number(this.select.value)));
    }

    handleKeydown = (event) => {
        const moves = {
            ArrowRight: 1,
            ArrowDown: 1,
            ArrowLeft: -1,
            ArrowUp: -1,
        };

        let next = null;

        if (event.key in moves) {
            // Counts from `target`, not `index`, for the reason show() documents: `index`
            // is 280ms behind during a fade, so two quick presses both resolved to the
            // same tab and the second was swallowed by the guard, while Right-then-Left
            // wrapped to the last tab instead of returning to the first. This is the same
            // fix step() carries in feature-carousel.js — it was applied there and missed
            // here, because the carousel steps through one method and the tabs through
            // two.
            const from = this.target ?? this.index;

            next = (from + moves[event.key] + this.tabs.length) % this.tabs.length;
        } else if (event.key === "Home") {
            next = 0;
        } else if (event.key === "End") {
            next = this.tabs.length - 1;
        }

        if (next === null) return;

        // Only now, so an unhandled key — Tab above all — keeps its default behaviour.
        event.preventDefault();
        this.show(next);
        this.tabs[next].focus();
    };

    show(next, { animate = true } = {}) {
        // Two pieces of state, because for the 280ms of a fade they genuinely differ:
        // `target` is the panel that has been asked for, `index` the one still on screen.
        // Guarding on `index` meant a click back to the current tab inside that window
        // was swallowed as a no-op while the fade it should have cancelled still landed —
        // on tab 1, clicking tab 2 then tab 1 within 280ms ended on tab 2. `target` is set
        // synchronously here and committed to `index` by swap().
        if (next === this.target) return;

        this.target = next;
        clearTimeout(this.fadeTimer);

        // Synchronously, with `target`: the picker names what was asked for, so a tab
        // chosen on a wide screen is already selected if the window narrows mid-fade.
        if (this.select) this.select.value = String(next);

        const swap = () => {
            this.panels.forEach((panel, i) => {
                panel.classList.toggle("is-inactive", i !== next);
                panel.classList.remove("is-fading");
            });

            this.tabs.forEach((tab, i) => {
                tab.setAttribute("aria-selected", String(i === next));
                // -1 rather than removing the attribute: a tab that is not current stays
                // reachable by the arrow keys but out of the page's Tab order.
                tab.tabIndex = i === next ? 0 : -1;
            });

            this.index = next;
        };

        // `next === this.index` is the cancel case: the panel asked for is the one already
        // on screen, so a fade in flight is being undone rather than a new one started.
        // swap() is the whole undo — it clears is-fading and re-asserts aria-selected and
        // the roving tabindex, which the cancelled swap would otherwise have left on the
        // outgoing tab.
        if (!animate || prefersReducedMotion() || next === this.index) {
            swap();
            return;
        }

        this.panels[this.index].classList.add("is-fading");
        this.fadeTimer = setTimeout(swap, FADE_MS);
    }
}

document.querySelectorAll("[data-feature-tabs]").forEach((elem) => new FeatureTabs(elem));
